import React, { type FC, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ToDo } from 'components/ToDos';
import { DashboardToDosGrid } from '../DashboardToDosGrid';
import { useTranslation } from 'react-i18next';
import { Schedule, useScheduleQuery } from 'components/Calendaring';
import { useAuth } from 'components/Auth';
import { AverageCompletedSessions } from 'components/Dashboard/AverageCompletedSessions';
import { EMP_TYPE_FULLTIME, EMP_TYPE_PARTTIME } from 'components/ProviderProfile/constants';
import { LiabilityInsuranceAlert } from 'components/ProviderProfile/components/Sections/LiabilityInsurance';
import { useCredentialing } from 'components/ProviderProfile/components/Tabs/Credentialing/CredentialingContext';
import { Spinner } from 'components/Shared';
import { LicensesAndRegistrationsAlert } from 'components/ProviderProfile/components/Sections/LicensesAndRegistrations/LicensesAndRegistrationsAlert';
import { CIReferralsGrid } from 'components/CriticalIncident/components/CIReferralsGrid';
import { useSessionFlag } from 'hooks/useSessionFlag';
import { useIsTraumaCounsellor } from 'components/CriticalIncident/hooks/useIsTraumaCounsellor';
import { useFeatureManager } from 'hooks/useFeatureManager/useFeatureManager';

interface DashboardBodyProps {
    toDos: ToDo[] | undefined,
    loading: boolean
}

export const DashboardBody: FC<DashboardBodyProps> = ({toDos, loading}) => {
    const { t } = useTranslation('dashboard', { useSuspense: false });
    const { effectiveProviderData } = useAuth();
    const { setLiabilityInsuranceHandleRenew, credentialsLoading } = useCredentialing();
    const enableCriticalIncident = useSessionFlag<boolean | undefined>('enable-critical-incident');
    const { isCriticalIncidentFeatureEnabled } = useFeatureManager();
    const { isTraumaCounsellor, isMixedCounsellor, isEAPCounsellor, loading: loadingContractedServices } = useIsTraumaCounsellor(effectiveProviderData?.providerId!);
    const hasNoContractedServices = !isTraumaCounsellor && !isEAPCounsellor;
    const showEAPFeatures = isEAPCounsellor || isMixedCounsellor || hasNoContractedServices;
    const showCIFeatures = isCriticalIncidentFeatureEnabled() && (isTraumaCounsellor || isMixedCounsellor || enableCriticalIncident);
    const navigate = useNavigate();
    const onRenewClick = () => navigate('/profile/credentialing', { state: { renewInsurance: true } });
    const onRenewLicenseClick = (renewLicenseId: number) => navigate('/profile/credentialing', { state: { renewLicenseId } });
    setLiabilityInsuranceHandleRenew(onRenewClick);

    const scheduleQuery = useScheduleQuery(!showEAPFeatures);
    const unavailabilities = useMemo(() =>
        scheduleQuery.data?.schedule?.unavailabilities?.map(unavailability => ({
            ...unavailability,
            unavailabilityReason: scheduleQuery?.data?.schedule?.unavailabilityReasons?.find(reason => reason.id === unavailability.unavailabilityReasonID)
        }))
    , [scheduleQuery.data]);

    return (
        <div className="dashboard_body_element">
            {
                showEAPFeatures && effectiveProviderData?.hasManagedCalendar &&
                <div className="dashboard_schedule_section">
                    {[EMP_TYPE_FULLTIME, EMP_TYPE_PARTTIME].includes(effectiveProviderData?.employmentTypeId ?? 0) &&
                        <>
                            <h2>{t('average_completed_sessions')}</h2>
                            <AverageCompletedSessions
                                loading={scheduleQuery.loading}
                                providerId={effectiveProviderData?.providerId}
                            />
                        </>
                    }
                    <h2>{t('upcoming_appointments')}</h2>
                    <Schedule loading={scheduleQuery.loading}
                        unavailabilities={unavailabilities}
                        availabilities={scheduleQuery.data?.schedule?.availabilities ?? []}
                    />
                </div>
            }
            <div className="dashboard_todos_section outstanding_to_do_element">
                {credentialsLoading ? <div className="alert-loading"><Spinner /></div> : (
                    <>
                        <LiabilityInsuranceAlert />
                        <LicensesAndRegistrationsAlert type="expired" onRenewClick={onRenewLicenseClick} />
                        <LicensesAndRegistrationsAlert type="expiring" onRenewClick={onRenewLicenseClick} />
                    </>
                )}
                {loadingContractedServices ? <Spinner /> : showCIFeatures && <CIReferralsGrid />}
                {showEAPFeatures && (
                    <>
                        <div className="title_link_wrapper">
                            <h2>{t('dashboard_todos_section_header', 'To-dos')}</h2>
                            <Link className="viewAll__link" to={{ pathname: '/todos' }}>
                                {t('view_all', 'View all')} ({toDos?.length || 0})
                            </Link>
                        </div>
                        <DashboardToDosGrid toDos={toDos} loading={loading} />
                    </>
                )}
            </div>
        </div>  
    );
}